import * as React from 'react';
import { Icon } from '../../components/icons/Icon';
import { cn, getTestId, type TestIdProps } from '../../utils';

export interface FiltersCollapsibleProps extends Pick<TestIdProps, 'testId' | 'id'> {
  /** Text of the toggle button. */
  label: React.ReactNode;
  /** How many of the filters inside are set; a badge on the toggle shows it while above zero. */
  activeCount?: number;
  /** Open on mount when uncontrolled. */
  defaultOpen?: boolean;
  /** Controlled open state; pair with `onOpenChange`. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  /** Filter controls shown while open; unmounted while closed. */
  children?: React.ReactNode;
}

/**
 * A toggle that hides a group of filters behind one button, with a count of the active ones.
 * Meant for `Filters` children, below the always-visible search.
 */
export const FiltersCollapsible = React.forwardRef<HTMLDivElement, FiltersCollapsibleProps>(
  (
    {
      label,
      activeCount = 0,
      defaultOpen = false,
      open: controlledOpen,
      onOpenChange,
      className,
      children,
      testId,
      id
    },
    ref
  ) => {
    const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
    const open = controlledOpen ?? uncontrolledOpen;
    const panelId = React.useId();
    const resolvedTestId = getTestId({ testId, id });

    const toggle = () => {
      const next = !open;
      if (controlledOpen === undefined) setUncontrolledOpen(next);
      onOpenChange?.(next);
    };

    return (
      <div ref={ref} id={id} data-testid={resolvedTestId} className={cn('space-y-md', className)}>
        <button
          type='button'
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          onClick={toggle}
          data-testid={resolvedTestId && `${resolvedTestId}-toggle`}
          className={cn(
            'flex w-full items-center gap-sm rounded-md py-xs text-left text-sm font-semibold',
            'text-foreground-base hover:text-primary-base cursor-pointer',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-base'
          )}
        >
          <Icon name='sliders' className='h-3.5! w-3.5! shrink-0 text-foreground-muted' />
          <span className='min-w-0 flex-1'>{label}</span>
          {activeCount > 0 && (
            <span
              data-testid={resolvedTestId && `${resolvedTestId}-count`}
              className='inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary-base px-2xs text-2xs font-semibold leading-none text-foreground-white'
            >
              {activeCount}
            </span>
          )}
          <Icon
            name='chevron-down'
            aria-hidden='true'
            className={cn(
              'h-3.5! w-3.5! shrink-0 text-foreground-muted transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </button>

        {open && (
          <div id={panelId} data-testid={resolvedTestId && `${resolvedTestId}-panel`}>
            {children}
          </div>
        )}
      </div>
    );
  }
);

FiltersCollapsible.displayName = 'FiltersCollapsible';
